import NavbarTop from "../ui_components/navbar_top";
import { useParams } from 'react-router-dom';
import React, { useEffect, useState, useMemo } from 'react';
import Editor from '@monaco-editor/react';
import classNames from 'classnames';
import { JAVA, ALL_LANGUAGES, PYTHON, errorMsg, handleCtrlSave, homeUrl, success, isBlankOrNullString, getYoutubeVideoId, isYoutubeVideo } from "../utils/common_utils";
import { getAuth, getCurrrentLanguage, getUIState, isLoggedIn, isValidUser, saveCurrentLanguage, saveUser } from "../login/auth_utils";
import IndefiniteProgressBar from "../ui_components/indefinite_progress_bar";
import { QuestionNotesLink } from "./question_notes";
import { YoutubeVideoDisplay } from "../ui_components/youtube_video_display";
import { LinkUI, LinkUIBasic, LinkUIBlueBold } from "../ui_components/common_links";
import { txb, txbb, txrb } from "../ui_components/text_style_components";
import { JavaIcon, PythonIcon, YouTubeIcon } from "../ui_components/editorial_icons";

/**
 * This function fetches the list of all solutions for the given question . These can be code blogs, youtube videos etc
 */
function QuestionSolutions({question}) {
  const [loading, setLoading] = useState(false);
  const[error, setError] = useState('')
  const[message, setMessage] = useState('')
  const[solutions, setSolutions] = useState([])

 function fetchSolutions(questionId){
  console.log(" fetching solutions for question "+questionId)
  setLoading(true)
  fetch(homeUrl()+"questionsApi/questionSolution/"+questionId, {
    method: "GET"
  })
    .then((response) => response.json())
    .then((data) => {
      setLoading(false)
      console.log("fetched question solutions \n"+JSON.stringify(data))
      if(success(data.status) && data.list && data.list.length>0){
        var arr=[]
        for(let i=0;i<data.list.length;i++)
            arr.push(JSON.parse(data.list[i]))
        setSolutions(arr)
      }
      if(!success(data.status)){
        setError(data.message || "Error in fetching editorial, please try again")
      }
      // fetch successful , but no solutions available
      if(success(data.status) && ( !data.list || data.list.length==0)){
         setError("As of now, no solutions have been written for this question.")
      }
       // console.log("response received")
       // console.log(data);
    })
    .catch((error) => {
      console.log("error occured in fetching solutions "+JSON.stringify(error));
      setError(errorMsg())
      setLoading(false)
    });
 }

 function getPatternsString(patterns){
  if(!patterns||patterns.indexOf(",")<0) return patterns
  let str=""
  let words=patterns.split(",")
  for(let i=0;i<words.length-2;i++)str=str+words[i].trim()+", "
  let len=words.length
  return str+words[len-2].trim()+" and "+words[len-1].trim()
 }


// loading metadata and code
useEffect(() => {
    console.log("showing solution page "+question.heading)
  if(question && question.questionId && question.questionId>0){
    fetchSolutions(question.questionId)
  }

}, []); // empty bracket means there are no dependencies which will make useEffect run only once and not re run again and again

  return (
    <div className="mr-4 flex min-w-0 flex-col overflow-y-auto overflow-x-hidden px-3 pb-6 sm:px-4">
      <div className="border-b border-slate-200 pb-4">
        <div className="text-3xl font-bold leading-7 text-slate-900">
          {question.questionId}. &nbsp; {question.heading}
        </div>
      </div>

      {question && question.designPatterns && question.designPatterns.length > 0 && (
        <div className="mt-6 flex flex-wrap items-center gap-2 text-sm">
          <span className="rounded-full bg-emerald-50 px-3 py-1.5 font-semibold text-emerald-700">
            Design patterns used
          </span>

          <span className="rounded-full bg-slate-100 px-3 py-1.5 font-medium text-slate-600">
            {getPatternsString(question.designPatterns)}
          </span>
        </div>
      )}

      {loading && (
        <div className="m-4 flex items-center justify-center px-4 py-4 text-center">
          <span className="z-50">
            <IndefiniteProgressBar />
          </span>
        </div>
      )}

      <div className="mt-3">
        {error && (
          <div className="rounded-xl bg-red-50 px-4 py-2 text-sm italic text-red-700">
            {error}
          </div>
        )}

        {message && (
          <div className="rounded-xl bg-indigo-50 px-4 py-2 text-sm text-indigo-700">
            {message}
          </div>
        )}
      </div>

      {solutions && solutions.length > 0 && (
        <div className="mb-6 mt-8 text-xl font-bold text-slate-900">
          Editorial solutions for this question: 
        </div>
      )}

      {solutions && solutions.length > 0 && solutions.map((row, index) => {
        const IconComponent = getSolutionIcon(row.text, row.url);
        const youtubeVideo = isYoutubeVideo(row.url);

        return (
          <div
            key={index}
            className="mb-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm sm:p-5"
          >
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
              <div className="flex min-w-0 flex-1 basis-64 items-center gap-3 sm:gap-4">
                {IconComponent && (
                  <IconComponent className="h-16 w-16" />
                )}

                <div className="min-w-0 flex-1">
                  {row.text && (
                    <div
                      className="break-words text-base font-semibold leading-6 text-slate-900"
                      dangerouslySetInnerHTML={{ __html: row.text }}
                    />
                  )}

                 {false && <div className="mt-1 text-sm leading-6 text-slate-500">
                    {youtubeVideo
                      ? "Watch the solution explained step by step"
                      : "Step-by-step explanation with code"}
                  </div>}
                </div>
              </div>

              {row.url && (
                <a
                  href={row.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto inline-flex shrink-0 items-center gap-2 rounded-md text-sm font-semibold text-emerald-700 hover:text-emerald-900 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-emerald-600"
                >
                  {youtubeVideo ? "Watch on YouTube" : "Read tutorial"}

                  <span aria-hidden="true" className="text-xl leading-none">
                    {youtubeVideo ? "↗" : "→"}
                  </span>
                </a>
              )}
            </div>

            {youtubeVideo && (
              <div className="mt-4 overflow-hidden rounded-xl bg-slate-50 p-1">
                <YoutubeVideoDisplay videoId={getYoutubeVideoId(row.url)} />
              </div>
            )}
          </div>
        );
      })}

      <div className="mt-5 border-t border-slate-200 pt-4">
        <div className="flex items-start gap-3 rounded-2xl bg-emerald-50 px-4 py-4">
          <span
            aria-hidden="true"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-lg font-semibold text-emerald-700"
          >
            i
          </span>

          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900">
              New to CodeZym?
            </div>

            <div className="mt-1 text-sm leading-6 text-slate-600">
              For a quick introduction, review{" "}
              <a
                href="https://codezym.com/questionnotes/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-emerald-700 underline underline-offset-2 hover:text-emerald-900"
              >
                Important Notes
              </a>{" "}
              and watch{" "}
              <a
                href="https://youtu.be/N5AIiVjGzHQ"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-emerald-700 underline underline-offset-2 hover:text-emerald-900"
              >
                Getting Started with CodeZym
              </a>.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function getSolutionIcon(title, url) {
  const text = String(title || "")
    .replace(/<[^>]*>/g, "")
    .toLowerCase();

  const link = String(url || "").toLowerCase();

  if (
    text.includes("youtube") ||
    link.includes("youtube") ||
    link.includes("youtu.be")
  ) {
    return YouTubeIcon;
  }

  if (text.includes("java")) return JavaIcon;
  if (text.includes("python")) return PythonIcon;

  return null;
}

export {QuestionSolutions}